import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { 
  ArrowLeft, Car, Plus, Edit2, Trash2, DollarSign, MapPin, 
  Route, Users, Loader2, Save, Clock, Upload, Image, X
} from "lucide-react";
import AddressAutocomplete from "../components/AddressAutocomplete";

const SettingsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("vehicles");
  const [vehicles, setVehicles] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Vehicle form state
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [vehicleForm, setVehicleForm] = useState({
    name: "",
    slug: "",
    base_price: "",
    per_km_rate: "",
    minimum_fare: "",
    hourly_rate: "",
    min_hours: "2",
    capacity: "",
    description: "",
    image_url: ""
  });
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  
  // Route form state
  const [routeDialogOpen, setRouteDialogOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [routeForm, setRouteForm] = useState({
    origin: "",
    destination: "",
    vehicle_type_slug: "",
    fixed_price: "",
    distance_km: "",
    notes: ""
  });
  
  // Multi-vehicle route pricing state
  const [multiVehiclePricing, setMultiVehiclePricing] = useState(false);
  const [vehiclePrices, setVehiclePrices] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [vehiclesRes, routesRes] = await Promise.all([
        api.get("/vehicles?include_inactive=true"),
        api.get("/routes")
      ]);
      setVehicles(vehiclesRes.data);
      setRoutes(routesRes.data);
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  // Vehicle handlers
  const openVehicleDialog = (vehicle = null) => {
    if (vehicle) {
      setEditingVehicle(vehicle);
      setVehicleForm({
        name: vehicle.name,
        slug: vehicle.slug,
        base_price: vehicle.base_price.toString(),
        per_km_rate: vehicle.per_km_rate.toString(),
        minimum_fare: (vehicle.minimum_fare || 0).toString(),
        hourly_rate: (vehicle.hourly_rate || 0).toString(),
        min_hours: (vehicle.min_hours || 2).toString(),
        capacity: vehicle.capacity.toString(),
        description: vehicle.description || "",
        image_url: vehicle.image_url || ""
      });
      // Clear any previous file selection
      setSelectedImageFile(null);
      setImagePreview(null);
    } else {
      setEditingVehicle(null);
      setVehicleForm({
        name: "",
        slug: "",
        base_price: "",
        per_km_rate: "",
        minimum_fare: "",
        hourly_rate: "",
        min_hours: "2",
        capacity: "",
        description: "",
        image_url: ""
      });
    }
    setVehicleDialogOpen(true);
  };

  // Image file handling
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error("Please select a JPEG, PNG, or WebP image");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setSelectedImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const clearSelectedImage = () => {
    setSelectedImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadVehicleImage = async (vehicleId) => {
    if (!selectedImageFile) return null;

    const formData = new FormData();
    formData.append('file', selectedImageFile);

    try {
      const response = await api.post(`/vehicles/${vehicleId}/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.image_url;
    } catch (error) {
      console.error("Image upload failed:", error);
      throw error;
    }
  };

  const handleVehicleSubmit = async () => {
    if (!vehicleForm.name || !vehicleForm.base_price || !vehicleForm.per_km_rate || !vehicleForm.capacity) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      const data = {
        name: vehicleForm.name,
        slug: vehicleForm.slug || vehicleForm.name.toLowerCase().replace(/\s+/g, "_"),
        base_price: parseFloat(vehicleForm.base_price),
        per_km_rate: parseFloat(vehicleForm.per_km_rate),
        minimum_fare: parseFloat(vehicleForm.minimum_fare) || 0,
        hourly_rate: parseFloat(vehicleForm.hourly_rate) || 0,
        min_hours: parseInt(vehicleForm.min_hours) || 2,
        capacity: parseInt(vehicleForm.capacity),
        description: vehicleForm.description,
        image_url: vehicleForm.image_url || null,
        is_active: true
      };

      let vehicleId;
      if (editingVehicle) {
        await api.put(`/vehicles/${editingVehicle.id}`, data);
        vehicleId = editingVehicle.id;
        toast.success("Vehicle type updated");
      } else {
        const response = await api.post("/vehicles", data);
        vehicleId = response.data.id;
        toast.success("Vehicle type created");
      }

      // Upload image if selected
      if (selectedImageFile && vehicleId) {
        setUploadingImage(true);
        try {
          await uploadVehicleImage(vehicleId);
          toast.success("Image uploaded successfully");
        } catch {
          toast.error("Vehicle saved but image upload failed");
        }
        setUploadingImage(false);
      }
      
      setVehicleDialogOpen(false);
      clearSelectedImage();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save vehicle type");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVehicle = async (vehicleId) => {
    if (!window.confirm("Are you sure you want to deactivate this vehicle type?")) return;
    
    try {
      await api.delete(`/vehicles/${vehicleId}`);
      toast.success("Vehicle type deactivated");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete vehicle type");
    }
  };

  // Route handlers
  const openRouteDialog = (route = null) => {
    if (route) {
      setEditingRoute(route);
      setRouteForm({
        origin: route.origin,
        destination: route.destination,
        vehicle_type_slug: route.vehicle_type_slug,
        fixed_price: route.fixed_price.toString(),
        distance_km: route.distance_km?.toString() || "",
        notes: route.notes || ""
      });
      setMultiVehiclePricing(false);
      setVehiclePrices({});
    } else {
      setEditingRoute(null);
      setRouteForm({
        origin: "",
        destination: "",
        vehicle_type_slug: "",
        fixed_price: "",
        distance_km: "",
        notes: ""
      });
      setMultiVehiclePricing(false);
      // Initialize vehicle prices for all active vehicles
      const initialPrices = {};
      vehicles.filter(v => v.is_active).forEach(v => {
        initialPrices[v.slug] = "";
      });
      setVehiclePrices(initialPrices);
    }
    setRouteDialogOpen(true);
  };

  const handleRouteSubmit = async () => {
    if (!routeForm.origin || !routeForm.destination) {
      toast.error("Please enter origin and destination");
      return;
    }

    // Validate based on mode
    if (multiVehiclePricing) {
      const hasAtLeastOnePrice = Object.values(vehiclePrices).some(p => p && parseFloat(p) > 0);
      if (!hasAtLeastOnePrice) {
        toast.error("Please enter at least one vehicle price");
        return;
      }
    } else {
      if (!routeForm.vehicle_type_slug || !routeForm.fixed_price) {
        toast.error("Please select a vehicle and enter price");
        return;
      }
    }

    setSaving(true);
    try {
      if (multiVehiclePricing && !editingRoute) {
        // Create multiple routes - one for each vehicle with a price
        const routesToCreate = [];
        for (const [slug, price] of Object.entries(vehiclePrices)) {
          if (price && parseFloat(price) > 0) {
            routesToCreate.push({
              origin: routeForm.origin,
              destination: routeForm.destination,
              vehicle_type_slug: slug,
              fixed_price: parseFloat(price),
              distance_km: routeForm.distance_km ? parseFloat(routeForm.distance_km) : null,
              notes: routeForm.notes
            });
          }
        }
        
        // Create all routes
        await Promise.all(routesToCreate.map(data => api.post("/routes", data)));
        toast.success(`Created ${routesToCreate.length} route prices`);
      } else {
        // Single route mode
        const data = {
          origin: routeForm.origin,
          destination: routeForm.destination,
          vehicle_type_slug: routeForm.vehicle_type_slug,
          fixed_price: parseFloat(routeForm.fixed_price),
          distance_km: routeForm.distance_km ? parseFloat(routeForm.distance_km) : null,
          notes: routeForm.notes
        };

        if (editingRoute) {
          await api.put(`/routes/${editingRoute.id}`, data);
          toast.success("Route price updated");
        } else {
          await api.post("/routes", data);
          toast.success("Route price created");
        }
      }
      
      setRouteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save route price");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRoute = async (routeId) => {
    if (!window.confirm("Are you sure you want to delete this route price?")) return;
    
    try {
      await api.delete(`/routes/${routeId}`);
      toast.success("Route price deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete route price");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <button onClick={() => navigate("/admin")} className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" />
              <span>Back to Dashboard</span>
            </button>
            <div className="flex items-center gap-2">
              <Car className="h-6 w-6 text-primary" />
              <span className="font-bold">Settings</span>
            </div>
            <div className="w-32" />
          </div>
        </div>
      </header>

      <main className="pt-20 pb-8 px-4 max-w-6xl mx-auto">
        <h1 className="text-2xl lg:text-3xl font-bold mb-2">Pricing & Vehicles</h1>
        <p className="text-muted-foreground mb-8">Manage vehicle types and route pricing</p>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("vehicles")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              activeTab === "vehicles" ? "bg-primary text-primary-foreground" : "bg-secondary/50 text-muted-foreground hover:bg-secondary"
            }`}
            data-testid="tab-vehicles"
          >
            <Car className="h-4 w-4 inline mr-2" />
            Vehicle Types
          </button>
          <button
            onClick={() => setActiveTab("routes")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              activeTab === "routes" ? "bg-primary text-primary-foreground" : "bg-secondary/50 text-muted-foreground hover:bg-secondary"
            }`}
            data-testid="tab-routes"
          >
            <Route className="h-4 w-4 inline mr-2" />
            Fixed Route Prices
          </button>
        </div>

        {/* Vehicle Types Tab */}
        {activeTab === "vehicles" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <p className="text-muted-foreground">
                Set base prices and per-kilometer rates for each vehicle type
              </p>
              <Dialog open={vehicleDialogOpen} onOpenChange={setVehicleDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={() => openVehicleDialog()} className="bg-primary text-primary-foreground" data-testid="add-vehicle-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Vehicle Type
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-black/10">
                  <DialogHeader>
                    <DialogTitle>{editingVehicle ? "Edit Vehicle Type" : "Add Vehicle Type"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Name *</Label>
                        <Input
                          placeholder="e.g., Sedan"
                          value={vehicleForm.name}
                          onChange={(e) => setVehicleForm({ ...vehicleForm, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, "_") })}
                          className="bg-secondary/50 border-black/10"
                          data-testid="vehicle-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Slug</Label>
                        <Input
                          placeholder="sedan"
                          value={vehicleForm.slug}
                          onChange={(e) => setVehicleForm({ ...vehicleForm, slug: e.target.value.toLowerCase().replace(/\s+/g, "_") })}
                          className="bg-secondary/50 border-black/10"
                          disabled={!!editingVehicle}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Base Price ($) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="75.00"
                          value={vehicleForm.base_price}
                          onChange={(e) => setVehicleForm({ ...vehicleForm, base_price: e.target.value })}
                          className="bg-secondary/50 border-black/10"
                          data-testid="vehicle-base-price-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Per KM Rate ($) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="2.50"
                          value={vehicleForm.per_km_rate}
                          onChange={(e) => setVehicleForm({ ...vehicleForm, per_km_rate: e.target.value })}
                          className="bg-secondary/50 border-black/10"
                          data-testid="vehicle-per-km-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Minimum Fare ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={vehicleForm.minimum_fare}
                          onChange={(e) => setVehicleForm({ ...vehicleForm, minimum_fare: e.target.value })}
                          className="bg-secondary/50 border-black/10"
                          data-testid="vehicle-minimum-fare-input"
                        />
                        <p className="text-xs text-muted-foreground">Price won't go below this amount</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Capacity *</Label>
                        <Input
                          type="number"
                          min="1"
                          placeholder="4"
                          value={vehicleForm.capacity}
                          onChange={(e) => setVehicleForm({ ...vehicleForm, capacity: e.target.value })}
                          className="bg-secondary/50 border-black/10"
                          data-testid="vehicle-capacity-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Hourly Rate ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="65.00"
                          value={vehicleForm.hourly_rate}
                          onChange={(e) => setVehicleForm({ ...vehicleForm, hourly_rate: e.target.value })}
                          className="bg-secondary/50 border-black/10"
                          data-testid="vehicle-hourly-rate-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Min Hours</Label>
                        <Input
                          type="number"
                          min="1"
                          placeholder="2"
                          value={vehicleForm.min_hours}
                          onChange={(e) => setVehicleForm({ ...vehicleForm, min_hours: e.target.value })}
                          className="bg-secondary/50 border-black/10"
                          data-testid="vehicle-min-hours-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Vehicle Image</Label>
                      <div className="flex flex-col gap-3">
                        {/* File Upload */}
                        <div 
                          className="border-2 border-dashed border-white/20 rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            onChange={handleImageSelect}
                            className="hidden"
                            data-testid="vehicle-image-file-input"
                          />
                          <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">
                            Click to upload image
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            JPEG, PNG or WebP (max 5MB)
                          </p>
                        </div>

                        {/* Image Preview */}
                        {(imagePreview || vehicleForm.image_url) && (
                          <div className="relative p-3 bg-secondary/30 rounded-lg">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                clearSelectedImage();
                                if (!selectedImageFile) {
                                  setVehicleForm({ ...vehicleForm, image_url: '' });
                                }
                              }}
                              className="absolute top-2 right-2 p-1 bg-red-500/80 hover:bg-red-500 rounded-full"
                            >
                              <X className="h-4 w-4 text-white" />
                            </button>
                            <p className="text-xs text-muted-foreground mb-2">
                              {selectedImageFile ? 'New Image:' : 'Current Image:'}
                            </p>
                            <img 
                              src={imagePreview || vehicleForm.image_url} 
                              alt="Vehicle preview" 
                              className="h-24 w-auto object-contain mx-auto rounded"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                            {selectedImageFile && (
                              <p className="text-xs text-center text-muted-foreground mt-2">
                                {selectedImageFile.name}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        placeholder="Comfortable sedan for business travel..."
                        value={vehicleForm.description}
                        onChange={(e) => setVehicleForm({ ...vehicleForm, description: e.target.value })}
                        className="bg-secondary/50 border-black/10"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button onClick={handleVehicleSubmit} disabled={saving || uploadingImage} className="bg-primary text-primary-foreground" data-testid="save-vehicle-btn">
                      {(saving || uploadingImage) ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          {uploadingImage ? 'Uploading...' : 'Saving...'}
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Save
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid gap-4">
              {vehicles.map(vehicle => (
                <Card key={vehicle.id} className={`dashboard-card ${!vehicle.is_active ? "opacity-50" : ""}`} data-testid={`vehicle-card-${vehicle.slug}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {vehicle.image_url ? (
                          <div className="w-16 h-12 rounded-lg overflow-hidden bg-secondary/30">
                            <img 
                              src={vehicle.image_url} 
                              alt={vehicle.name} 
                              className="w-full h-full object-contain"
                            />
                          </div>
                        ) : (
                          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Car className="h-6 w-6 text-primary" />
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-lg">{vehicle.name}</h3>
                            {!vehicle.is_active && <Badge variant="outline" className="text-red-400">Inactive</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground">{vehicle.description || `Capacity: ${vehicle.capacity} passengers`}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Base Price</p>
                          <p className="text-lg font-bold text-primary">${vehicle.base_price.toFixed(2)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Per KM</p>
                          <p className="text-lg font-bold">${vehicle.per_km_rate.toFixed(2)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Min Fare</p>
                          <p className="text-lg font-bold text-orange-600">${(vehicle.minimum_fare || 0).toFixed(2)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Hourly</p>
                          <p className="text-lg font-bold text-accent">${(vehicle.hourly_rate || 0).toFixed(2)}</p>
                          <p className="text-xs text-muted-foreground">min {vehicle.min_hours || 2}h</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Capacity</p>
                          <p className="text-lg font-bold flex items-center gap-1">
                            <Users className="h-4 w-4" /> {vehicle.capacity}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => openVehicleDialog(vehicle)} data-testid={`edit-vehicle-${vehicle.slug}`}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          {vehicle.is_active && (
                            <Button size="sm" variant="outline" className="text-destructive" onClick={() => handleDeleteVehicle(vehicle.id)} data-testid={`delete-vehicle-${vehicle.slug}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Price Calculator Info */}
            <Card className="dashboard-card bg-primary/5 border-primary/20">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-primary" />
                  How Pricing Works
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
                  <div>
                    <p className="font-medium text-foreground mb-1">Transfer Booking</p>
                    <p><strong>Calculated Price</strong> = Base Price + (Distance × Per KM Rate)</p>
                    <p className="mt-1 text-orange-600"><strong>Minimum Fare</strong> = If calculated price is below minimum fare, the minimum fare applies</p>
                    <p className="mt-1">Example: 30km in Sedan = $75 + (30 × $2.50) = <strong className="text-primary">$150.00</strong></p>
                  </div>
                  <div>
                    <p className="font-medium text-foreground mb-1">Hourly Booking</p>
                    <p><strong>Total</strong> = Hourly Rate × Number of Hours</p>
                    <p className="mt-1">Example: 4 hours in Sedan = $65 × 4 = <strong className="text-accent">$260.00</strong></p>
                  </div>
                </div>
                <p className="text-muted-foreground text-sm mt-3">
                  <strong>Fixed Route Prices</strong> override calculated prices for specific routes (e.g., Airport → CBD).
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Fixed Route Prices Tab */}
        {activeTab === "routes" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <p className="text-muted-foreground">
                Set fixed prices for specific routes (overrides per-km calculation)
              </p>
              <Dialog open={routeDialogOpen} onOpenChange={setRouteDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={() => openRouteDialog()} className="bg-primary text-primary-foreground" data-testid="add-route-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Route Price
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-black/10 max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>{editingRoute ? "Edit Route Price" : "Add Route Price"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    {/* Origin & Destination with Autocomplete */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Origin Location *</Label>
                        <AddressAutocomplete
                          value={routeForm.origin}
                          onChange={(value) => setRouteForm({ ...routeForm, origin: value })}
                          onSuggestionSelect={(value) => setRouteForm({ ...routeForm, origin: value })}
                          placeholder="Search origin (e.g., BNE, Sydney Airport)"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Destination *</Label>
                        <AddressAutocomplete
                          value={routeForm.destination}
                          onChange={(value) => setRouteForm({ ...routeForm, destination: value })}
                          onSuggestionSelect={(value) => setRouteForm({ ...routeForm, destination: value })}
                          placeholder="Search destination (e.g., Hilton Surfers)"
                        />
                      </div>
                    </div>
                    
                    {/* Distance & Notes */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Distance (KM)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          placeholder="25"
                          value={routeForm.distance_km}
                          onChange={(e) => setRouteForm({ ...routeForm, distance_km: e.target.value })}
                          className="bg-secondary/50 border-black/10"
                          data-testid="route-distance-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Notes</Label>
                        <Input
                          placeholder="Optional notes"
                          value={routeForm.notes}
                          onChange={(e) => setRouteForm({ ...routeForm, notes: e.target.value })}
                          className="bg-secondary/50 border-black/10"
                        />
                      </div>
                    </div>
                    
                    {/* Multi-Vehicle Toggle (only for new routes) */}
                    {!editingRoute && (
                      <div className="flex items-center justify-between p-3 bg-primary/10 rounded-lg border border-primary/30">
                        <div>
                          <Label className="font-medium">Set prices for all vehicles</Label>
                          <p className="text-xs text-muted-foreground">Create route prices for multiple vehicle types at once</p>
                        </div>
                        <Switch
                          checked={multiVehiclePricing}
                          onCheckedChange={setMultiVehiclePricing}
                          data-testid="multi-vehicle-toggle"
                        />
                      </div>
                    )}
                    
                    {/* Single Vehicle Mode */}
                    {(!multiVehiclePricing || editingRoute) && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Vehicle Type *</Label>
                          <Select value={routeForm.vehicle_type_slug} onValueChange={(v) => setRouteForm({ ...routeForm, vehicle_type_slug: v })}>
                            <SelectTrigger className="bg-secondary/50 border-black/10" data-testid="route-vehicle-select">
                              <SelectValue placeholder="Select vehicle" />
                            </SelectTrigger>
                            <SelectContent>
                              {vehicles.filter(v => v.is_active).map(v => (
                                <SelectItem key={v.slug} value={v.slug}>{v.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Fixed Price ($) *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="85.00"
                            value={routeForm.fixed_price}
                            onChange={(e) => setRouteForm({ ...routeForm, fixed_price: e.target.value })}
                            className="bg-secondary/50 border-black/10"
                            data-testid="route-price-input"
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Multi-Vehicle Pricing Mode */}
                    {multiVehiclePricing && !editingRoute && (
                      <div className="space-y-3">
                        <Label className="text-sm font-medium">Vehicle Prices</Label>
                        <div className="grid grid-cols-2 gap-3">
                          {vehicles.filter(v => v.is_active).map(v => (
                            <div key={v.slug} className="flex items-center gap-2 p-2 bg-secondary/30 rounded-lg">
                              <Car className="h-4 w-4 text-primary flex-shrink-0" />
                              <span className="text-sm flex-1">{v.name}</span>
                              <div className="relative w-24">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={vehiclePrices[v.slug] || ""}
                                  onChange={(e) => setVehiclePrices({ ...vehiclePrices, [v.slug]: e.target.value })}
                                  className="bg-secondary/50 border-black/10 pl-6 h-8 text-sm"
                                  data-testid={`route-price-${v.slug}`}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground">Leave empty to skip a vehicle type</p>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button onClick={handleRouteSubmit} disabled={saving} className="bg-primary text-primary-foreground" data-testid="save-route-btn">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                      Save
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {routes.length === 0 ? (
              <Card className="dashboard-card">
                <CardContent className="py-12 text-center">
                  <Route className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">No fixed route prices configured</p>
                  <Button onClick={() => openRouteDialog()} className="bg-primary text-primary-foreground">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Your First Route
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Route</th>
                      <th>Vehicle</th>
                      <th>Distance</th>
                      <th>Fixed Price</th>
                      <th>Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {routes.map(route => (
                      <tr key={route.id}>
                        <td>
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-green-500" />
                            <span>{route.origin}</span>
                            <span className="text-muted-foreground">→</span>
                            <MapPin className="h-4 w-4 text-red-500" />
                            <span>{route.destination}</span>
                          </div>
                        </td>
                        <td>
                          <Badge variant="outline">{route.vehicle_type_name}</Badge>
                        </td>
                        <td>{route.distance_km ? `${route.distance_km} km` : "-"}</td>
                        <td className="font-bold text-primary">${route.fixed_price.toFixed(2)}</td>
                        <td className="text-muted-foreground text-sm max-w-[200px] truncate">{route.notes || "-"}</td>
                        <td>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => openRouteDialog(route)} data-testid={`edit-route-${route.id}`}>
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline" className="text-destructive" onClick={() => handleDeleteRoute(route.id)} data-testid={`delete-route-${route.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Common Routes Suggestions */}
            <Card className="dashboard-card bg-accent/5 border-accent/20">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-accent" />
                  Common Routes to Consider
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-muted-foreground">
                  <p>• Sydney Airport → Sydney CBD</p>
                  <p>• Sydney Airport → North Sydney</p>
                  <p>• Sydney Airport → Parramatta</p>
                  <p>• Melbourne Airport → Melbourne CBD</p>
                  <p>• Brisbane Airport → Brisbane CBD</p>
                  <p>• Perth Airport → Perth CBD</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default SettingsPage;
